import React, { useState } from 'react';
import { Ability, Player, AbilityExecution } from '../../types';

interface AbilityPanelProps {
  currentPlayer: Player;
  isCurrentPlayerTurn: boolean;
  onAbilitySelect: (ability: Ability | null) => void;
  onExecuteAbility: (abilityId: string, targetPlayerIds: string[]) => Promise<AbilityExecution>;
  selectedAbility: Ability | null;
  availablePlayers: Player[];
}

export const AbilityPanel: React.FC<AbilityPanelProps> = ({
  currentPlayer,
  isCurrentPlayerTurn,
  onAbilitySelect,
  onExecuteAbility,
  selectedAbility,
  availablePlayers,
}) => {
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<AbilityExecution | null>(null);

  const formatRange = (range: { min: number; max: number; text?: string }) => {
    // If we have the original range text from lwdoc, use that as primary display
    if (range.text) {
      return range.text;
    }
    
    // Fallback to the computed range format
    if (range.min === range.max) {
      return `${range.min} pace${range.min !== 1 ? 's' : ''}`;
    }
    return `${range.min}-${range.max} paces`;
  };

  if (!currentPlayer.character) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-gray-500">No character selected</p>
      </div>
    );
  }

  const abilities = currentPlayer.character.abilities;

  const handleAbilityClick = (ability: Ability) => {
    if (!isCurrentPlayerTurn) return;
    
    // Check if player has enough phases
    if (currentPlayer.stats.phasesRemaining < ability.requiresPhases) {
      return;
    }

    // Check if ability has uses remaining
    if (ability.usesRemaining !== undefined && ability.usesRemaining !== null && ability.usesRemaining <= 0) {
      return;
    }

    onAbilitySelect(ability);
    setSelectedTargets([]);
    setExecutionResult(null);
  };

  const handleExecute = async () => {
    if (!selectedAbility || selectedTargets.length === 0) return;

    setIsExecuting(true);
    try {
      const result = await onExecuteAbility(selectedAbility.abilityId, selectedTargets);
      setExecutionResult(result);
      setSelectedTargets([]);
      onAbilitySelect(null); // Clear selection after execution
    } catch (error) {
      console.error('Failed to execute ability:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  const isAbilityUsable = (ability: Ability) => {
    if (!isCurrentPlayerTurn) return false;
    if (currentPlayer.stats.phasesRemaining < ability.requiresPhases) return false;
    if (ability.usesRemaining !== undefined && ability.usesRemaining !== null && ability.usesRemaining <= 0) return false;
    return true;
  };

  const getValidTargets = (ability: Ability) => {
    return availablePlayers.filter(player => {
      // Skip eliminated players
      if (player.stats.isEliminated) return false;

      // Check targeting rules
      for (const rule of ability.validTargets) {
        // Check character type restriction
        if (rule.characterType && player.character?.type !== rule.characterType) {
          continue;
        }

        // Check exclude self rule
        if (rule.excludeSelf && player.userId === currentPlayer.userId) {
          continue;
        }

        return true;
      }

      // If no rules match, allow targeting
      return ability.validTargets.length === 0;
    });
  };

  const getMaxTargets = (ability: Ability) => {
    const maxTargetsRule = ability.validTargets.find(rule => rule.maxTargets);
    return maxTargetsRule?.maxTargets || getValidTargets(ability).length;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">
        Abilities {!isCurrentPlayerTurn && '(Not your turn)'}
      </h3>

      {/* Phases Remaining */}
      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="font-medium text-blue-900">Phases Remaining:</span>
          <span className="text-2xl font-bold text-blue-600">
            {currentPlayer.stats.phasesRemaining}
          </span>
        </div>
      </div>

      {/* Ability List */}
      <div className="space-y-3 mb-6">
        {abilities.map((ability) => {
          const usable = isAbilityUsable(ability);
          const isSelected = selectedAbility?.abilityId === ability.abilityId;

          return (
            <div
              key={ability.abilityId}
              className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : usable
                  ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
              }`}
              onClick={() => handleAbilityClick(ability)}
            >
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-gray-900">{ability.name}</h4>
                <div className="flex gap-2 text-sm">
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                    {ability.requiresPhases} phase{ability.requiresPhases !== 1 ? 's' : ''}
                  </span>
                  {ability.usesRemaining !== undefined && ability.usesRemaining !== null && (
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {ability.usesRemaining} left
                    </span>
                  )}
                </div>
              </div>
              
              <p className="text-gray-600 text-sm mb-2">{ability.description}</p>
              
              {ability.maxUses !== undefined && ability.maxUses !== null && (
                <p className="text-xs mb-2" style={{ color: '#f4d03f' }}>
                  Max Uses: {ability.maxUses}
                </p>
              )}
              
              <div className="flex justify-between text-sm text-gray-500">
                {ability.damage > 0 && (
                  <span>Damage: {ability.damage}</span>
                )}
                <span>Range: {formatRange(ability.range)}</span>
                <span>
                  {ability.requiresAttackRoll 
                    ? (ability.isWillAttack ? 'Will attack' : 'Attack roll required') 
                    : 'No roll'}
                </span>
              </div>
              
              {ability.isWillAttack && (
                <div className="mt-2">
                  <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded font-semibold">
                    ⚡ Will Attack - Uses Will stat for attack/defense
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Target Selection */}
      {selectedAbility && (
        <div className="border-t pt-6">
          <h4 className="font-semibold text-gray-900 mb-3">
            Select Targets for {selectedAbility.name}
          </h4>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Max targets: {getMaxTargets(selectedAbility)}
            </p>
            
            <div className="space-y-2">
              {getValidTargets(selectedAbility).map((player) => (
                <label
                  key={player.userId}
                  className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTargets.includes(player.userId)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        if (selectedTargets.length < getMaxTargets(selectedAbility)) {
                          setSelectedTargets([...selectedTargets, player.userId]);
                        }
                      } else {
                        setSelectedTargets(selectedTargets.filter(id => id !== player.userId));
                      }
                    }}
                    disabled={
                      !selectedTargets.includes(player.userId) &&
                      selectedTargets.length >= getMaxTargets(selectedAbility)
                    }
                    className="mr-3"
                  />
                  
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">
                        {player.username} ({player.character?.name})
                      </span>
                      <div className="flex gap-4 text-sm text-gray-600">
                        <span>HP: {player.stats.health}</span>
                        <span>Will: {player.stats.will}</span>
                        <span>Att: +{player.stats.attackBonus}</span>
                        <span>Def: +{player.stats.defenseBonus}</span>
                      </div>
                    </div>
                    
                    {player.stats.activeEffects.length > 0 && (
                      <div className="mt-1">
                        {player.stats.activeEffects.map((effect, index) => (
                          <span
                            key={index}
                            className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded mr-1"
                          >
                            {effect.type}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Execute Button */}
          <button
            onClick={handleExecute}
            disabled={selectedTargets.length === 0 || isExecuting}
            className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
              selectedTargets.length === 0 || isExecuting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            {isExecuting ? 'Executing...' : `Execute ${selectedAbility.name}`}
          </button>
        </div>
      )}

      {/* Execution Results */}
      {executionResult && (
        <div className="border-t pt-6 mt-6">
          <h4 className="font-semibold text-gray-900 mb-3">Execution Results</h4>
          
          {/* Attack Roll */}
          {executionResult.attackRoll && (
            <div className="mb-4 p-3 bg-green-50 rounded-lg">
              <h5 className="font-medium text-green-900 mb-2">Attack Roll</h5>
              <div className="flex items-center gap-2 text-sm">
                <span>Dice: [{executionResult.attackRoll.dice.join(', ')}]</span>
                <span>+ Bonus: {executionResult.attackRoll.bonus}</span>
                <span className="font-bold">= Total: {executionResult.attackRoll.total}</span>
              </div>
            </div>
          )}

          {/* Defense Rolls */}
          {executionResult.defenseRolls && executionResult.defenseRolls.size > 0 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <h5 className="font-medium text-blue-900 mb-2">Defense Rolls</h5>
              {Array.from(executionResult.defenseRolls.entries()).map(([playerId, roll]) => {
                const player = availablePlayers.find(p => p.userId === playerId);
                return (
                  <div key={playerId} className="flex items-center gap-2 text-sm mb-1">
                    <span className="font-medium">{player?.username}:</span>
                    <span>Dice: [{roll.dice.join(', ')}]</span>
                    <span>+ Bonus: {roll.bonus}</span>
                    <span className="font-bold">= Total: {roll.total}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Results */}
          <div className="space-y-2">
            {executionResult.results.map((result, index) => {
              const player = availablePlayers.find(p => p.userId === result.targetPlayerId);
              return (
                <div
                  key={index}
                  className={`p-3 rounded-lg ${
                    result.hit ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{player?.username}</span>
                    <span className={`font-bold ${result.hit ? 'text-red-600' : 'text-gray-600'}`}>
                      {result.hit ? 'HIT' : 'MISS'}
                    </span>
                  </div>
                  
                  {result.hit && (
                    <div className="mt-1 text-sm">
                      <div>Damage dealt: {result.damageDealt}</div>
                      {result.effectsApplied.length > 0 && (
                        <div>Effects: {result.effectsApplied.join(', ')}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};